/**
 * Static placeholder content for the public site shell.
 *
 * Dynamic data (gallery images, pricing, FAQ entries, payment labels) will be
 * loaded from the HTTP API in later tasks (18.2, 18.4, 18.6, 18.7). Until then
 * these constants provide representative defaults so the shell renders fully.
 */

import type { FaqEntry, PricingConfig } from "@photography-booking/shared";

/** Sections reachable from the top navigation, in display order. */
export interface NavItem {
  /** DOM id of the target section. */
  id: string;
  /** Label shown in the nav bar. */
  label: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { id: "gallery", label: "GALLERY" },
  { id: "investment", label: "INVESTMENT" },
  { id: "about", label: "ABOUT" },
  { id: "faq", label: "FAQs" },
  { id: "booking", label: "BOOK" },
];

/** A social profile link rendered in the footer. */
export interface SocialLink {
  label: string;
  href: string;
}

export const SOCIAL_LINKS: readonly SocialLink[] = [
  { label: "Instagram", href: "https://www.instagram.com/hypermewww" },
];

export const STUDIO_NAME = "hypermewww";
export const STUDIO_TAGLINE = "Fashion & Lifestyle Modeling";

/**
 * Default investment pricing: per-shoot-type hourly rates. Monetary values are
 * integer cents to match {@link PricingConfig}; the live values arrive from the
 * pricing API. The owner can adjust these in the admin portal.
 *  - Bikini: $125/hr
 *  - Fashion: $100/hr
 *  - Runway: $40/hr
 */
export const DEFAULT_PRICING: PricingConfig = {
  rates: [
    { label: "Swim", hourlyRateCents: 12_500 },
    { label: "Fashion", hourlyRateCents: 10_000 },
  ],
  updatedAt: "1970-01-01T00:00:00.000Z",
};

/** Placeholder About copy until owner-managed content is wired in. */
export const ABOUT_HEADING = "About";
export const ABOUT_BODY: readonly string[] = [
  "hypermewww is a fashion and lifestyle model working across editorial, commercial, and campaign shoots — from denim and everyday wear to swimwear, dresses, and statement pieces.",
  "Book a session to feature your brand or collection. Every booking starts with a quick chat about the look you're after, then we lock in a date that works for both of us.",
];

/** Placeholder FAQ entries until the FAQ API is wired in (task 18.4). */
export const DEFAULT_FAQS: readonly FaqEntry[] = [
  {
    faqId: "faq-shoots",
    question: "What kind of shoots do you book?",
    answer:
      "Editorial, commercial, and campaign work across a range of styles — casual and denim, formalwear and dresses, swimwear, and brand lookbooks. If you're unsure whether your concept is a fit, just ask when you submit an inquiry.",
    order: 0,
  },
  {
    faqId: "faq-booking",
    question: "How does booking work?",
    answer:
      "Pick an available date and send an inquiry. You'll get a confirmation, and once the date is accepted you'll receive the payment options to reserve it. Availability shown here only reflects open or booked days — never any personal schedule details.",
    order: 1,
  },
  {
    faqId: "faq-payment",
    question: "What payment methods do you accept?",
    answer:
      "Cash, Cash App, or Zelle. Payment is arranged directly with the model — either at the time of the shoot, or when a deposit is needed to hold your date. No payment is taken through this website.",
    order: 2,
  },
];
